import { useEffect, useState } from 'react';
import { Card } from '@iwoca/orion';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { useQuery } from '@tanstack/react-query';
import { fetchGetFormalOffers } from '@iwoca/lapi-client/edge';
import styles from './xsell.module.css';
import RblCard from './rblCard/rblCard';
import FlexiCard from './flexiCard/flexiCard';
import XsellTooptip from './xsellTooltip/xsellTooltip';
import { XSellTitle } from './xsellTitle/xsellTitle';
import FlexiInterestOnlyCard from './flexiInterestOnlyCard/flexiInterestOnlyCard';
import { useSplitTests } from '@api/lendingAPI/lapiHooks';
import { NewChoiceUI } from './NewChoiceUI';
import {
    getAvailableCash,
    getMinAvailableCash,
    getMaxAvailableCash,
    isRBLOffer
} from '../../MyOffers.helpers';
import { LoadingAnimation } from '../../../ui/loadingAnimation/LoadingAnimation';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { ChooseLoanAmount } from '../../../sharedDashboardComponents/slider/chooseLoanAmount/ChooseLoanAmount';
import { getXSellOfferType } from './getXSellType';
import { isInterestOnlyOffer } from './xsell.helpers';

export const XSell = () => {
    const stateKey = useStateKey();

    const showNewUI = useSplitTests('new_approval_ui_test_2024');

    const [drawdownAmount, setDrawdownAmount] = useState<number>();

    // Data Fetching
    const {
        isLoading,
        isError,
        data: getFormalOffersResponse
    } = useQuery({
        queryKey: ['formalOffers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey: stateKey }),
        enabled: !!stateKey
    });

    useEffect(() => {
        if (drawdownAmount === undefined && getFormalOffersResponse?.data) {
            const maxAmountforAllOffers = getMinAvailableCash(
                getFormalOffersResponse!.data!.formal_offers
            );
            if (maxAmountforAllOffers) {
                setDrawdownAmount(Math.floor(maxAmountforAllOffers));
            }
        }
    }, [getFormalOffersResponse, drawdownAmount, setDrawdownAmount]);

    if (isLoading) {
        return <LoadingAnimation />;
    }

    //if getFormalOffersResponse is an empty formal offers array, show an error screen
    if (
        isError ||
        !getFormalOffersResponse.data ||
        getFormalOffersResponse.data.formal_offers.length === 0
    ) {
        return <FallbackErrorModal />;
    }

    const hasRBLOffer = (): boolean => {
        return Boolean(
            getFormalOffersResponse.data?.formal_offers.some((offer) =>
                isRBLOffer(offer)
            )
        );
    };

    //Show different component for users assigned to new ui split test and are not RBL offers
    if (showNewUI && !hasRBLOffer()) {
        return <NewChoiceUI formalOffers={getFormalOffersResponse.data} />;
    }

    const showCards = () => {
        const xselltype = getXSellOfferType(
            getFormalOffersResponse.data!.formal_offers
        );
        const flexiOffers = getFormalOffersResponse?.data?.formal_offers.filter(
            (fo) =>
                fo.product_type === 'credit_facility' &&
                !isInterestOnlyOffer(fo) &&
                fo.duration?.amount! < 731
        );

        const longerLoansOffer =
            getFormalOffersResponse?.data?.formal_offers.filter(
                (fo) =>
                    fo.product_type === 'credit_facility' &&
                    !isInterestOnlyOffer(fo) &&
                    fo.duration?.amount! > 730
            );

        const flexiInterestOnlyOffer =
            getFormalOffersResponse?.data?.formal_offers.find(
                (fo) =>
                    fo.product_type === 'credit_facility' &&
                    (fo.interest_only_repayments || 0) > 0
            );

        const rblOffer = getFormalOffersResponse?.data?.formal_offers.find(
            (fo) => fo.product_type === 'merchant_cash_advance'
        );

        switch (xselltype) {
            case 'dual_choice':
                return getFormalOffersResponse?.data?.formal_offers.map(
                    (offer) => (
                        <FlexiCard
                            key={offer.offer_id}
                            offers={offer}
                            drawdownAmount={drawdownAmount}
                            copyMode={
                                offer.duration?.amount! > 365
                                    ? 'flexi24_xsell'
                                    : 'default'
                            }
                        />
                    )
                );
            case 'interest-only_only':
                return (
                    <FlexiInterestOnlyCard
                        offer={flexiInterestOnlyOffer!}
                        amount={drawdownAmount}
                    />
                );
            case 'duration_choice':
                return (
                    <>
                        <FlexiCard
                            offers={flexiOffers!}
                            drawdownAmount={drawdownAmount}
                            copyMode="shorter_loans_xsell"
                        />

                        <FlexiCard
                            offers={longerLoansOffer!}
                            drawdownAmount={drawdownAmount}
                            copyMode="longer_loans_xsell"
                        />
                    </>
                );
            case 'x_sell':
            default:
                return (
                    <>
                        <FlexiCard
                            offers={flexiOffers!}
                            drawdownAmount={drawdownAmount}
                            copyMode={
                                rblOffer ? 'rbl_xsell' : 'interest_only_xsell'
                            }
                        />
                        {rblOffer && (
                            <RblCard
                                key={rblOffer.offer_id}
                                offer={rblOffer}
                                amount={drawdownAmount}
                            />
                        )}
                        {flexiInterestOnlyOffer && (
                            <FlexiInterestOnlyCard
                                offer={flexiInterestOnlyOffer}
                                amount={drawdownAmount}
                            />
                        )}
                    </>
                );
        }
    };

    const expiryDate = getFormalOffersResponse.data.formal_offers[0].timeout;

    const maxCashAmount = getAvailableCash(
        getFormalOffersResponse.data?.formal_offers!
    );

    const absMax = getMaxAvailableCash(
        getFormalOffersResponse.data.formal_offers
    );

    return (
        <section className={styles.xsell}>
            <XSellTitle expiryDate={expiryDate} maxCashAmount={maxCashAmount} />

            <div className={styles.cards}>
                <Card
                    className={styles.drawdown}
                    data-testid="drawdownAmountCard"
                >
                    {drawdownAmount && (
                        <ChooseLoanAmount
                            className="flex w-full flex-col sm:flex-row"
                            setDrawdownAmount={(value) =>
                                setDrawdownAmount(value)
                            }
                            drawdownAmount={drawdownAmount}
                            minimumDrawdownAmount={150}
                            maximumDrawdownAmount={absMax}
                            inputTooltip={
                                <XsellTooptip
                                    offers={
                                        getFormalOffersResponse.data
                                            .formal_offers
                                    }
                                />
                            }
                        />
                    )}
                </Card>
                {showCards()}
            </div>
        </section>
    );
};
